<template>
  <div :class="rootClasses">
    <PageHeader
      v-if="!embedded"
      title="Resume"
      description="Professional background, experience, and qualifications."
      :breadcrumbs="[{ label: 'Home', to: '/' }, { label: 'Resume' }]"
    >
      <template #actions>
        <BaseButton :loading="isLoading" variant="primary" @click="downloadResume">
          <AppIcon name="Download" :size="16" />
          {{ isLoading ? 'Preparing PDF...' : 'Download Resume' }}
        </BaseButton>
      </template>
    </PageHeader>
    <SectionHeader
      v-else
      title="Resume"
      description="Professional background, experience, and qualifications."
    >
      <template #actions>
        <BaseButton :loading="isLoading" variant="primary" @click="downloadResume">
          <AppIcon name="Download" :size="16" />
          {{ isLoading ? 'Preparing PDF...' : 'Download Resume' }}
        </BaseButton>
      </template>
    </SectionHeader>

    <BaseCard class="mb-8" title="Professional Summary">
      <p class="text-body mt-2">{{ profile.informations.summary }}</p>
      <p class="text-caption mt-3">{{ profile.informations.objectives }}</p>
    </BaseCard>

    <BaseCard title="Work Experience" description="3+ years at Innsite IT Solution and related development work." class="mb-8">
      <div class="relative mt-4 space-y-8">
        <div
          v-for="(job, index) in experience"
          :key="index"
          class="relative pl-6 border-l-2 border-primary/30 pb-2 last:pb-0"
        >
          <div class="absolute -left-[5px] top-1.5 w-2 h-2 rounded-full bg-primary" aria-hidden="true" />
          <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
            <div>
              <h4 class="text-h5">{{ job.role }}</h4>
              <p class="text-sm text-muted">{{ job.company }} · {{ job.location }}</p>
            </div>
            <BaseBadge variant="primary" size="sm">
              {{ job.duration ? `${job.period} · ${job.duration}` : job.period }}
            </BaseBadge>
          </div>
          <ul class="mt-3 space-y-2">
            <li v-for="(point, i) in job.highlights" :key="i" class="flex items-start gap-2 text-sm text-foreground">
              <CheckCircle class="h-4 w-4 text-primary shrink-0 mt-0.5" />
              <span>{{ point }}</span>
            </li>
          </ul>
        </div>
      </div>
    </BaseCard>

    <div class="grid lg:grid-cols-2 gap-8 mb-8">
      <BaseCard title="Education" description="Academic background">
        <div class="relative mt-4">
          <div class="absolute left-4 top-0 bottom-0 w-px bg-border" aria-hidden="true" />
          <div v-for="(edu, index) in education" :key="index" class="relative pl-10 pb-8 last:pb-0">
            <div
              class="absolute left-2.5 w-3 h-3 rounded-full bg-primary border-2 border-surface"
              aria-hidden="true"
            />
            <BaseBadge variant="primary" size="sm">{{ edu.period }}</BaseBadge>
            <h4 class="text-h5 mt-2">{{ edu.level }}</h4>
            <p class="text-sm font-medium text-foreground mt-1">{{ edu.school }}</p>
            <p class="text-caption flex items-center gap-1 mt-1">
              <AppIcon name="MapPin" :size="14" />
              {{ edu.location }}
            </p>
          </div>
        </div>
      </BaseCard>

      <BaseCard title="Contact & Details">
        <dl class="grid sm:grid-cols-2 gap-3 mt-2">
          <div v-for="item in personalInfo" :key="item.label" class="p-3 rounded-lg bg-border-subtle/50">
            <dt class="text-xs text-muted">{{ item.label }}</dt>
            <dd class="text-sm font-medium mt-0.5">{{ item.value }}</dd>
          </div>
        </dl>
      </BaseCard>
    </div>

    <div class="grid lg:grid-cols-3 gap-8">
      <BaseCard title="Core Competencies">
        <ul class="space-y-2 mt-2">
          <li v-for="skill in personalSkills" :key="skill" class="flex items-center gap-2 text-sm">
            <CheckCircle class="h-4 w-4 text-success shrink-0" />
            {{ skill }}
          </li>
        </ul>
      </BaseCard>

      <BaseCard title="Interests">
        <ul class="space-y-2 mt-2">
          <li v-for="hobby in hobbies" :key="hobby" class="flex items-center gap-2 text-sm">
            <Heart class="h-4 w-4 text-danger shrink-0" />
            {{ hobby }}
          </li>
        </ul>
      </BaseCard>

      <BaseCard title="Technical Skills">
        <div class="grid grid-cols-1 gap-4 mt-2">
          <div v-for="section in technicalSkillSections" :key="section.key">
            <p class="text-xs font-semibold text-muted uppercase mb-2">{{ section.label }}</p>
            <div class="flex flex-wrap gap-2">
              <BaseBadge
                v-for="skill in section.items"
                :key="skill"
                :variant="section.variant"
                size="sm"
              >
                {{ skill.split(' ')[0] }}
              </BaseBadge>
            </div>
          </div>
        </div>
      </BaseCard>
    </div>
  </div>
</template>

<script>
import { CheckCircle, Heart } from '@lucide/vue'
import { siteState } from '@/composables/useSiteData'
import { images } from '@/data/images'
import { generateAndSaveResumePdf } from '@/utils/generateResumePdf'
import { useToast } from '@/composables/useToast'
import PageHeader from '@/components/layout/PageHeader.vue'
import SectionHeader from '@/components/layout/SectionHeader.vue'
import BaseCard from '@/components/ui/BaseCard.vue'
import BaseBadge from '@/components/ui/BaseBadge.vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import AppIcon from '@/components/ui/AppIcon.vue'

export default {
  name: 'ResumePage',
  components: { PageHeader, SectionHeader, BaseCard, BaseBadge, BaseButton, AppIcon, CheckCircle, Heart },
  props: {
    embedded: Boolean,
  },
  computed: {
    rootClasses() {
      return this.embedded
        ? 'page-container py-16 lg:py-20 animate-fade-in'
        : 'page-container animate-fade-in'
    },
    profile() {
      return siteState.profile
    },
    education() {
      return siteState.education
    },
    experience() {
      return siteState.experience
    },
    personalSkills() {
      return siteState.personalSkills
    },
    hobbies() {
      return siteState.hobbies
    },
    technicalSkills() {
      return siteState.technicalSkills
    },
    technicalSkillSections() {
      const labels = {
        frontend: 'Frontend',
        backend: 'Backend',
        ai: 'AI & Productivity',
        devops: 'DevOps',
      }
      const variants = {
        frontend: 'primary',
        backend: 'default',
        ai: 'success',
        devops: 'warning',
      }

      return Object.entries(this.technicalSkills || {})
        .filter(([, items]) => Array.isArray(items) && items.length)
        .map(([key, items]) => ({
          key,
          label: labels[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()),
          items,
          variant: variants[key] || 'default',
        }))
    },
    personalInfo() {
      const info = siteState.profile.informations
      return [
        { label: 'Email', value: info.email },
        { label: 'Phone', value: info.contact },
        { label: 'Location', value: info.homeaddress },
        { label: 'Website', value: info.Website },
        { label: 'Degree', value: info.degree },
        { label: 'Availability', value: info.freelance ? 'Open to freelance' : 'Not available' },
      ]
    },
  },
  data() {
    return {
      isLoading: false,
    }
  },
  methods: {
    async downloadResume() {
      this.isLoading = true
      const { success, error } = useToast()
      try {
        await generateAndSaveResumePdf({
          profile: siteState.profile,
          resumeEducation: siteState.resumeEducation,
          experience: siteState.experience,
          pdfSkills: siteState.pdfSkills,
          personalSkills: siteState.personalSkills,
          images,
        })
        success('Resume downloaded', 'Your PDF has been saved successfully.')
      } catch (e) {
        error('Download failed', 'Unable to generate the resume PDF. Please try again.')
        console.error(e)
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>
